import { Fragment, type FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { getAbsoluteUrl } from "shared/lib/utils";

import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { itemHomeV2AroundSCMPQueueItemsEdge$key } from "scmp-app/queries/__generated__/itemHomeV2AroundSCMPQueueItemsEdge.graphql";

import {
  Container,
  Content,
  ItemDivider,
  StyledContentItemHomeAroundMorningStudioPrimaryV2,
  StyledContentItemHomeAroundMorningStudioSecondaryV2,
  StyledContentItemHomeAroundPrimaryV2,
  StyledContentItemHomeAroundSecondaryV2,
  StyledScmpLabLogo,
  Title,
  TitleContainer,
} from "./styles";

const handleClick = (entityId = "", urlAlias = "") => {
  sendGA4Tracking({
    action: "click",
    category: "recirculation",
    customized_parameters: {
      article_id: entityId,
      destination_url: getAbsoluteUrl(urlAlias),
      page_type: "homepage",
      widget_name: "around_scmp",
    },
    subcategory: "widget",
  });
};

type Props = {
  className?: string;
  isMorningStudio?: boolean;
  reference: itemHomeV2AroundSCMPQueueItemsEdge$key;
  title: string;
  urlAlias: string;
  withSCMPLabLogo?: boolean;
};

export const HomeV2AroundItem: FunctionComponent<Props> = ({
  className,
  isMorningStudio,
  reference,
  title,
  urlAlias,
  withSCMPLabLogo,
}) => {
  const data = useFragment(
    graphql`
      fragment itemHomeV2AroundSCMPQueueItemsEdge on QueueItemsEdge @relay(plural: true) {
        node {
          ... on Content {
            entityId
            ...homeAroundMorningStudioPrimaryV2ContentItemContent
            ...homeAroundMorningStudioSecondaryV2ContentItemContent
            ...homeAroundPrimaryV2ContentItemContent
            ...homeAroundSecondaryV2ContentItemContent
          }
        }
      }
    `,
    reference,
  );

  const PrimaryComponent = isMorningStudio
    ? StyledContentItemHomeAroundMorningStudioPrimaryV2
    : StyledContentItemHomeAroundPrimaryV2;

  const SecondaryComponent = isMorningStudio
    ? StyledContentItemHomeAroundMorningStudioSecondaryV2
    : StyledContentItemHomeAroundSecondaryV2;

  return (
    <Container className={className}>
      <TitleContainer>
        <Title pathname={urlAlias}>{title}</Title>
        {withSCMPLabLogo && <StyledScmpLabLogo />}
      </TitleContainer>
      <Content>
        {data?.map((item, index) => (
          <Fragment key={item.node.entityId}>
            {index === 0 ? (
              <PrimaryComponent onClick={handleClick} reference={item.node} />
            ) : (
              <>
                <ItemDivider />
                <SecondaryComponent onClick={handleClick} reference={item.node} />
              </>
            )}
          </Fragment>
        ))}
      </Content>
    </Container>
  );
};

HomeV2AroundItem.displayName = "HomeV2AroundItem";
